// Pages that stop before the interface: one line of text instead of the module.
//
// - `outside` — the module address opened outside Bitrix24 (owner's decision @2026-10-05, letter
//   `frigatnet-staff-handover__outside-portal-page`): «О модуле» is the first screen only INSIDE the portal;
// - `noSdk` — inside a frame, but the portal SDK did not load or gave no `member_id` in time;
// - `kit` — the shared kit (kit.css / kit.js from our host) did not load (boot.ts). Without kit.css the interface is
//   unreadable, so «works without styles» is not an outcome.
//
// ⛔ The texts live HERE and not in the i18n dictionaries, and the box is styled inline: these pages are drawn before
// the shell and before the kit, and must open even when nothing else can. The language is the browser's.
// ⛔ Not a single request to the module server from these pages: the address is open to anyone and carries no
// `member_id` — a status request would go out without a tenant and read as «portal not connected».
import type { Lang } from '@frigat/kit';

export type BootMessage = 'outside' | 'noSdk' | 'kit';

const TEXT: Record<Lang, Record<BootMessage, string>> = {
  ru: {
    outside: 'Откройте приложение из Битрикс24.',
    noSdk: 'Не удалось связаться с Битрикс24. Обновите страницу.',
    kit: 'Не удалось загрузить интерфейс модуля. Обновите страницу.',
  },
  ua: {
    outside: 'Відкрийте застосунок з Бітрікс24.',
    noSdk: 'Не вдалося зв’язатися з Бітрікс24. Оновіть сторінку.',
    kit: 'Не вдалося завантажити інтерфейс модуля. Оновіть сторінку.',
  },
  en: {
    outside: 'Open the application from Bitrix24.',
    noSdk: 'Could not reach Bitrix24. Reload the page.',
    kit: 'Could not load the module interface. Reload the page.',
  },
};

/** Bitrix spells Ukrainian as 'ua', not ISO 'uk' (app-shell block §6) — do not "fix" it. */
export function normalizeLang(raw: string): Lang {
  const l = raw.toLowerCase();
  if (l === 'ua' || l === 'uk') return 'ua';
  if (l === 'en') return 'en';
  return 'ru';
}

/** Outside the portal there is no portal language: the browser's one decides. */
export function browserLang(nav: string): Lang {
  return normalizeLang(nav.slice(0, 2));
}

export function bootText(kind: BootMessage, lang: Lang): string {
  return TEXT[lang][kind];
}

/** Replaces the root with one line of text — its own inline look, no theme tokens (neither the shell nor the kit is up). */
export function renderBootMessage(root: HTMLElement, kind: BootMessage, lang: Lang): void {
  root.textContent = '';
  const box = document.createElement('div');
  box.style.cssText =
    'max-width:520px;margin:48px auto;padding:0 20px;text-align:center;' +
    'font:15px/1.55 system-ui,-apple-system,sans-serif;color:#1f2328';
  const title = document.createElement('h1');
  title.textContent = bootText(kind, lang);
  title.style.cssText = 'font-size:19px;font-weight:600;margin:0';
  box.append(title);
  root.append(box);
}
